/**
 * Bridges the Clerk session into the app's auth store.
 *
 * The whole app (AuthInitializer, ProtectedLayout, every store) keys off
 * `useAuthStore.user`. `users.ensureUser` both provisions the Convex row for a
 * Clerk identity and returns it, so one call does the work that used to take a
 * Convex mutation *and* a `GET /api/user/me` round trip through the api-server
 * and Supabase.
 *
 * Everything is gated on Convex reporting an authenticated client rather than
 * on Clerk alone: `ensureUser` calls `requireUser`, so firing it while the
 * token handshake is still in flight just fails and retries.
 */
import { useEffect, useRef } from 'react'
import { useAuth, useUser } from '@clerk/clerk-react'
import { useConvexAuth, useMutation } from 'convex/react'
import { api } from '@/convex/_generated/api'
import type { Doc } from '@/convex/_generated/dataModel'
import { setConvexAuthenticated } from '@/lib/convex-client'
import { useAuthStore, type AuthUser } from '@/stores/auth-store'

const MAX_ATTEMPTS = 5

function toAuthUser(doc: Doc<'users'>): AuthUser {
  return {
    id: doc.clerkUserId,
    email: doc.email,
    user_metadata: {
      name: doc.name,
      full_name: doc.name,
      avatar_url: doc.avatarUrl,
    },
    created_at: new Date(doc._creationTime).toISOString(),
  }
}

export function ClerkAuthBridge() {
  const { isLoaded, isSignedIn } = useAuth()
  const { user: clerkUser } = useUser()
  const inFlightRef = useRef(false)
  const ensureUser = useMutation(api.users.ensureUser)
  const { isAuthenticated: convexAuthenticated } = useConvexAuth()

  // Release imperative Convex callers (the Zustand stores) only once the client
  // is actually carrying a token; otherwise their first reads race the handshake.
  useEffect(() => {
    setConvexAuthenticated(convexAuthenticated)
  }, [convexAuthenticated])

  // Clerk resolved and nobody is signed in: clear the store and stop loading,
  // otherwise the guest routes would wait on a skeleton forever.
  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      useAuthStore.getState().setUser(null)
      useAuthStore.getState().setIsLoading(false)
    }
  }, [isLoaded, isSignedIn])

  // Provision (or link) the Convex user row, then publish it to the store.
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !convexAuthenticated) return

    let cancelled = false
    let attempts = 0

    const sync = async () => {
      if (inFlightRef.current) return
      inFlightRef.current = true
      try {
        const doc = await ensureUser({})
        if (!cancelled && doc) {
          useAuthStore.getState().setUser(toAuthUser(doc))
          useAuthStore.getState().setIsLoading(false)
        }
      } catch (error) {
        attempts += 1
        console.error(`[ClerkAuthBridge] user sync failed (attempt ${attempts}):`, error)
        if (!cancelled && attempts < MAX_ATTEMPTS) {
          setTimeout(() => {
            if (!cancelled && useAuthStore.getState().user === null) void sync()
          }, 2000 * attempts)
        }
      } finally {
        inFlightRef.current = false
      }
    }

    void sync()

    return () => {
      cancelled = true
    }
  }, [isLoaded, isSignedIn, convexAuthenticated, clerkUser?.id, ensureUser])

  return null
}
